import { useState, useRef, useEffect } from "react";

/**
 * Inline autocomplete: suggests options as user types.
 * Options = string[] or { value, label }[].
 * onSelect(value) when user picks; value/onChange for controlled input.
 */
export default function AutoComplete({
  label,
  value,
  onChange,
  onSelect,
  options = [],
  placeholder = "",
  className = "",
  inputClassName = "",
  getOptionLabel = (opt) => (typeof opt === "string" ? opt : opt?.label ?? opt?.value ?? ""),
  getOptionValue = (opt) => (typeof opt === "string" ? opt : opt?.value ?? opt?.label ?? ""),
  filterOptions = (opts, q) => {
    const qq = (q || "").trim().toLowerCase();
    if (!qq) return opts.slice(0, 20);
    return opts.filter((o) => getOptionLabel(o).toLowerCase().includes(qq)).slice(0, 20);
  },
  minChars = 0,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const containerRef = useRef(null);

  const filtered = filterOptions(options, value);
  const showDropdown = open && focused && (minChars === 0 || (value || "").length >= minChars);

  useEffect(() => {
    const h = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    setOpen(true);
  };

  const handleSelect = (opt) => {
    const label = getOptionLabel(opt);
    const val = getOptionValue(opt);
    onChange(label);
    onSelect?.(val);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-[#374151] mb-1">{label}</label>
      )}
      <input
        type="text"
        value={value}
        onChange={handleChange}
        onFocus={() => { setFocused(true); setOpen(true); }}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full border border-[#D1D5DB] rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none ${inputClassName}`}
        autoComplete="off"
      />
      {showDropdown && filtered.length > 0 && (
        <ul
          className="absolute z-50 mt-1 w-full max-h-48 overflow-auto rounded-lg border border-[#E5E7EB] bg-white shadow-lg py-1 text-sm"
          onMouseDown={(e) => e.preventDefault()}
        >
          {filtered.map((opt, i) => (
            <li
              key={i}
              role="option"
              onMouseDown={() => handleSelect(opt)}
              className="px-3 py-2 hover:bg-[#F3F4F6] cursor-pointer truncate"
            >
              {getOptionLabel(opt)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
